import { EMAIL_VALIDATION_REGEX } from 'constants/regex';
import { APPLE_TEAM_ID } from 'constants/teamId';
import { isEmpty, keyBy, mapValues } from 'lodash';
import debounce from 'lodash/debounce';
import React from 'react';
import { tzMoment } from 'utils/moment';

export const padArray = <T, U = undefined>(arr: T[], len: number, fill?: U): (T | U)[] =>
    arr.length > len ? arr : arr.concat(Array(len).fill(fill)).slice(0, len);

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const downloadFromUrl = (url: string) => {
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', url);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const getFirstMatchingKey = <T extends object, U extends object>(obj1: T, obj2: U) => {
    for (const key of Object.keys(obj1)) {
        if (obj2[key as keyof U] !== null) {
            return key;
        }
    }

    return 'default';
};

export const matchLastOrDefault = <TValue>(
    valuesDict: { [key: string]: TValue },
    props: Record<keyof typeof valuesDict, boolean | undefined> & { [key: string]: unknown }
): TValue =>
    valuesDict[
        Object.keys(props)
            .reverse()
            .filter(key => props[key] === true && valuesDict[key] !== undefined)[0]
    ] || valuesDict.default;

export const emailIsValid = (email: string) => EMAIL_VALIDATION_REGEX.test(email);

export const parcelType = (parcel: Bizly.Parcel) => parcel.type;

export const formatCurrency = (
    currency: Nullable<number>,
    currencyCode = 'USD',
    withCents: 'as-needed' | 'always' | undefined = 'as-needed'
): string => {
    if (currency || currency === 0) {
        const result = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
            ...(!withCents ? { maximumFractionDigits: 0 } : {}),
        }).format(currency);

        if (withCents) {
            const [whole, fraction] = result.split('.'); // TODO: refactor if not using 'en-US' because it'll be $100,00
            if (withCents === 'as-needed' && parseInt(fraction) === 0) {
                return whole;
            }
        }

        return result;
    } else {
        return '0';
    }
};

export const formatPercentage = (percentage: Nullable<number>): string =>
    percentage ? percentage.toString() + '%' : '0%';

export const formatDate = (dateAsString: string) => tzMoment(dateAsString).format('LT');

const METERS_PER_MILE = 1609.34;
const KILOMETERS_PER_MILE = 1.60934;
const KILOMETERS_PER_METER = 1000;

export function toMeters(miles: Distance.Mile): Distance.Meter {
    return (Math.round(miles * METERS_PER_MILE * 10) / 10) as Distance.Meter;
}

export function toMiles(meters: Distance.Meter): Distance.Mile {
    return (Math.round((meters / METERS_PER_MILE) * 10) / 10) as Distance.Mile;
}

export function kmToMi(km: Distance.Kilometer): Distance.Mile {
    return Math.floor(km / KILOMETERS_PER_MILE) as Distance.Mile;
}

export function miToKm(miles: Distance.Mile): Distance.Kilometer {
    return Math.ceil(miles * KILOMETERS_PER_MILE) as Distance.Kilometer;
}

export function mToKm(meters: Distance.Meter): Distance.Kilometer {
    return Math.floor(meters / KILOMETERS_PER_METER) as Distance.Kilometer;
}

export function kmToM(kilometers: Distance.Kilometer): Distance.Meter {
    return Math.ceil(kilometers * KILOMETERS_PER_METER) as Distance.Meter;
}

export const takeFirstIfArray = (params: { [param: string]: string | string[] | null | undefined }) =>
    Object.keys(params).reduce(
        (agg, param) => ({
            ...agg,
            [param]: Array.isArray(params[param] as string[]) ? params[param]![0] : params[param],
        }),
        params
    ) as { [param: string]: string | null | undefined };

export const fillObject = <TKey extends string, TFill>(keys: TKey[], fill: TFill) =>
    mapValues(keyBy(keys) as Record<TKey, TKey>, () => fill);

/*
    Debouncing is an async activity outside the React lifecycle
    Therefore, it is vulnerable to race conditions

    We will need to give the component a stable debounced function in which subsequent calls cancel prior calls
    The debounced function will need to call the *latest* version of the passed in callback

    Therefore, we must store the latest version of the callback
    But pass out a stable debounced function

    Limitations: changing the delay value will not update the function, this can be added as a future update
*/
export function useDebounce<T extends (...args: unknown[]) => unknown>(callback?: T, defaultDelay = 200) {
    const savedCallback = React.useRef(callback);
    React.useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    const debouncedFn = React.useMemo(
        () =>
            debounce((...args: unknown[]) => {
                // the callback may have changed by the time we hit this line, so we must use the latest version
                const currentCallback = savedCallback.current;
                if (currentCallback) {
                    return currentCallback(...(args ?? []));
                }
            }, defaultDelay),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultDelay changes won't update the function per design
        []
    );

    return [debouncedFn];
}

export const enableTurnDown =
    import.meta.env.VITE_APP_ENV === 'prod'
        ? import.meta.env.VITE_APP_TURN_DOWN_PROD === 'true'
        : import.meta.env.VITE_APP_TURN_DOWN === 'true';

export const hasSpecialCancellationEmail = function (
    setting?: Bizly.Team['specialCancellationEmail']
): setting is Exclude<Bizly.Team['specialCancellationEmail'], []> {
    return !!setting && !Array.isArray(setting);
};

export function numberWithCommas(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export const getLatestProposalRequest = (proposal: Bizly.VenueProposal) => {
    if (isEmpty(proposal.approvalRequests)) {
        return null;
    }

    const latestRequest = proposal.approvalRequests.reduce(
        (
            prev: Bizly.VenueProposal['approvalRequests'][number],
            current: Bizly.VenueProposal['approvalRequests'][number]
        ) => {
            return new Date(prev.updatedAt).getTime() > new Date(current.updatedAt).getTime() ? prev : current;
        },
        proposal.approvalRequests[0]
    );

    return latestRequest;
};

export const isAppleUser = (user: Bizly.User) => {
    if (!user) {
        return false;
    }
    return user.team?.id === APPLE_TEAM_ID;
};

export const isUserInTeam = (user: Bizly.User, teamId: number) => {
    if (!user) {
        return false;
    }
    return user.team?.id === teamId;
};

export const formatDateToShort = (dateString: string | null | undefined): string => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return dateString ? new Date(dateString).toLocaleDateString('en-US', options) : '';
};

export const isApplePreferred = (venue: Bizly.Venue): boolean => {
    return venue.preferred && venue.preferenceCategory?.name === 'Apple Preferred';
};

export function formatBytes(bytes: number | null | undefined, decimals = 2) {
    if (!bytes || bytes <= 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
